export interface TypographyToken {
  size: string
  weight: number
  lineHeight: number
  family?: string
}

export interface DesignTokens {
  colors: Record<string, string>
  spacing: Record<string, string>
  typography: Record<string, TypographyToken>
  radius: Record<string, string> | string
  shadow: Record<string, string>
  fontFamily?: string
  fontWeightDefault?: number
  resolve(path: string): unknown
}

interface RawDesignTokens {
  colors: Record<string, string>
  spacing: Record<string, string>
  typography: Record<string, TypographyToken>
  radius: Record<string, string> | string
  shadow: Record<string, string>
  fontFamily?: string
  fontWeightDefault?: number
}

export function loadDesignTokens(raw: RawDesignTokens): DesignTokens {
  return {
    ...raw,
    resolve(path: string): unknown {
      const keys = path.split(".")
      let current: unknown = raw
      for (const key of keys) {
        if (current == null || typeof current !== "object") return undefined
        current = (current as Record<string, unknown>)[key]
      }
      return current
    },
  }
}

export function mergeTokens(
  base: RawDesignTokens,
  override: Partial<RawDesignTokens>,
): RawDesignTokens {
  return {
    colors: { ...base.colors, ...override.colors },
    spacing: { ...base.spacing, ...override.spacing },
    typography: { ...base.typography, ...override.typography },
    radius: override.radius !== undefined ? override.radius : base.radius,
    shadow: { ...base.shadow, ...override.shadow },
    fontFamily: override.fontFamily ?? base.fontFamily,
    fontWeightDefault: override.fontWeightDefault ?? base.fontWeightDefault,
  }
}

// ---- Default theme constants (shadcn/ui) ----
// Complete set of CSS variable values so app content is fully isolated from
// the Studio shell theme. Values match globals.css :root / .dark blocks.

export const DEFAULT_LIGHT_THEME: Record<string, string> = {
  background: '0 0% 100%',
  foreground: '222.2 84% 4.9%',
  card: '0 0% 100%',
  'card-foreground': '222.2 84% 4.9%',
  popover: '0 0% 100%',
  'popover-foreground': '222.2 84% 4.9%',
  primary: '239 84% 67%',
  'primary-foreground': '0 0% 98%',
  secondary: '210 40% 96.1%',
  'secondary-foreground': '222.2 47.4% 11.2%',
  muted: '210 40% 96.1%',
  'muted-foreground': '215.4 16.3% 46.9%',
  accent: '210 40% 96.1%',
  'accent-foreground': '222.2 47.4% 11.2%',
  destructive: '0 84.2% 60.2%',
  'destructive-foreground': '0 0% 98%',
  border: '214.3 31.8% 91.4%',
  input: '214.3 31.8% 91.4%',
  ring: '239 84% 67%',
  'chart-1': '12 76% 61%',
  'chart-2': '173 58% 39%',
  'chart-3': '197 37% 24%',
  'chart-4': '43 74% 66%',
  'chart-5': '27 87% 67%',
}

export const DEFAULT_DARK_THEME: Record<string, string> = {
  background: '222.2 84% 4.9%',
  foreground: '210 40% 98%',
  card: '222.2 84% 4.9%',
  'card-foreground': '210 40% 98%',
  popover: '222.2 84% 4.9%',
  'popover-foreground': '210 40% 98%',
  primary: '239 84% 67%',
  'primary-foreground': '0 0% 98%',
  secondary: '217.2 32.6% 17.5%',
  'secondary-foreground': '210 40% 98%',
  muted: '217.2 32.6% 17.5%',
  'muted-foreground': '215 20.2% 65.1%',
  accent: '217.2 32.6% 17.5%',
  'accent-foreground': '210 40% 98%',
  destructive: '0 62.8% 30.6%',
  'destructive-foreground': '210 40% 98%',
  border: '217.2 32.6% 17.5%',
  input: '217.2 32.6% 17.5%',
  ring: '239 84% 67%',
  'chart-1': '220 70% 50%',
  'chart-2': '160 60% 45%',
  'chart-3': '30 80% 55%',
  'chart-4': '280 65% 60%',
  'chart-5': '340 75% 55%',
}

// ---- CSS custom property injection ----

/** Map from token camelCase color key → CSS variable name (without --) */
const COLOR_KEY_TO_CSS: Record<string, string> = {
  background: "background",
  foreground: "foreground",
  card: "card",
  cardForeground: "card-foreground",
  popover: "popover",
  popoverForeground: "popover-foreground",
  primary: "primary",
  primaryForeground: "primary-foreground",
  secondary: "secondary",
  secondaryForeground: "secondary-foreground",
  muted: "muted",
  mutedForeground: "muted-foreground",
  accent: "accent",
  accentForeground: "accent-foreground",
  destructive: "destructive",
  destructiveForeground: "destructive-foreground",
  border: "border",
  input: "input",
  ring: "ring",
  // Status tokens
  success: "success",
  successForeground: "success-foreground",
  warning: "warning",
  warningForeground: "warning-foreground",
  info: "info",
  infoForeground: "info-foreground",
  // Chart tokens (shadcn charts)
  chart1: "chart-1",
  chart2: "chart-2",
  chart3: "chart-3",
  chart4: "chart-4",
  chart5: "chart-5",
  // Sidebar tokens (shadcn sidebar)
  sidebarBackground: "sidebar-background",
  sidebarForeground: "sidebar-foreground",
  sidebarPrimary: "sidebar-primary",
  sidebarPrimaryForeground: "sidebar-primary-foreground",
  sidebarAccent: "sidebar-accent",
  sidebarAccentForeground: "sidebar-accent-foreground",
  sidebarBorder: "sidebar-border",
  sidebarRing: "sidebar-ring",
}

/** Check if a string is already in HSL channel format "H S% L%" */
const HSL_CHANNEL_RE = /^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/
function isHslChannels(value: string): boolean {
  return HSL_CHANNEL_RE.test(value.trim())
}

/** Resolve a color value from tokens, trying both camelCase key and kebab-case CSS var name */
function resolveColorValue(colors: Record<string, string>, camelKey: string, cssVar: string): string | undefined {
  return colors[camelKey] ?? colors[cssVar]
}

/** Convert hex (#RRGGBB or #RGB) to HSL channel string "H S% L%" for Tailwind. */
function hexToHslChannels(hex: string): string | null {
  const cleaned = hex.replace("#", "")
  let r: number, g: number, b: number
  if (cleaned.length === 3) {
    r = parseInt(cleaned[0] + cleaned[0], 16) / 255
    g = parseInt(cleaned[1] + cleaned[1], 16) / 255
    b = parseInt(cleaned[2] + cleaned[2], 16) / 255
  } else if (cleaned.length === 6) {
    r = parseInt(cleaned.slice(0, 2), 16) / 255
    g = parseInt(cleaned.slice(2, 4), 16) / 255
    b = parseInt(cleaned.slice(4, 6), 16) / 255
  } else {
    return null
  }

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0, s = 0

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    else if (max === g) h = ((b - r) / d + 2) / 6
    else h = ((r - g) / d + 4) / 6
  }

  const hDeg = Math.round(h * 3600) / 10
  const sPct = Math.round(s * 1000) / 10
  const lPct = Math.round(l * 1000) / 10
  return `${hDeg} ${sPct}% ${lPct}%`
}

/**
 * Build a CSS variable map from design tokens, suitable for element-scoped
 * inline styles. Returns e.g. { '--primary': '239 84% 67%', ... }
 */
export function getTokenCSSVars(tokens: { colors?: Record<string, string>; radius?: Record<string, string> | string; fontFamily?: string }): Record<string, string> {
  const vars: Record<string, string> = {}

  if (tokens.colors) {
    for (const [key, cssVar] of Object.entries(COLOR_KEY_TO_CSS)) {
      const raw = resolveColorValue(tokens.colors, key, cssVar)
      if (!raw) continue
      const hsl = isHslChannels(raw) ? raw.trim() : hexToHslChannels(raw)
      if (hsl) vars[`--${cssVar}`] = hsl
    }
  }

  if (tokens.radius) {
    if (typeof tokens.radius === 'string') {
      vars['--radius'] = tokens.radius
    } else {
      const radiusLg = tokens.radius.lg ?? tokens.radius.md
      if (radiusLg) vars['--radius'] = radiusLg
    }
  }

  if (tokens.fontFamily) {
    vars['--cogo-font-family'] = tokens.fontFamily
  }

  return vars
}

/**
 * Build a complete React CSSProperties object for scoping design tokens
 * to a container element. Includes both `--primary` (HSL channels) and
 * `--color-primary` (resolved hsl()) so Tailwind v4 utility classes work
 * even when tokens are set on a child element instead of :root.
 */
export function getScopedTokenStyle(tokens: { colors?: Record<string, string>; radius?: Record<string, string> | string; fontFamily?: string; fontWeightDefault?: number }): Record<string, string> {
  const style: Record<string, string> = {}

  if (tokens.colors) {
    for (const [key, cssVar] of Object.entries(COLOR_KEY_TO_CSS)) {
      const raw = resolveColorValue(tokens.colors, key, cssVar)
      if (!raw) continue
      const hsl = isHslChannels(raw) ? raw.trim() : hexToHslChannels(raw)
      if (hsl) {
        // Base channel var — `--primary: 239 84% 67%`
        style[`--${cssVar}`] = hsl
        // Resolved var for Tailwind v4 — `--color-primary: hsl(239 84% 67%)`
        // Tailwind v4 @theme registers `--color-primary: hsl(var(--primary))`
        // on :root. Registered properties compute at declaration time, so
        // overriding `--primary` on a child does NOT change `--color-primary`.
        // We must set both.
        style[`--color-${cssVar}`] = `hsl(${hsl})`
      }
    }
  }

  if (tokens.radius) {
    if (typeof tokens.radius === 'string') {
      style['--radius'] = tokens.radius
    } else {
      const radiusLg = tokens.radius.lg ?? tokens.radius.md
      if (radiusLg) style['--radius'] = radiusLg
    }
  }

  if (tokens.fontFamily) {
    style['--cogo-font-family'] = tokens.fontFamily
    style['fontFamily'] = `${tokens.fontFamily}, sans-serif`
  }

  if (tokens.fontWeightDefault) {
    style['fontWeight'] = String(tokens.fontWeightDefault)
  }

  return style
}

/**
 * Build a fully-isolated CSS style map for app content areas. Always returns a
 * complete set of CSS variables (never undefined), ensuring the app theme is
 * independent of the host shell (e.g. Studio dark/light toggle).
 *
 * 1. Start with a complete default theme (light or dark)
 * 2. Overlay any project-specific design tokens on top
 * 3. Set `colorScheme` for browser-level isolation (scrollbars, form controls)
 */
export function getIsolatedAppStyle(
  tokens?: { colors?: Record<string, string>; radius?: Record<string, string> | string; fontFamily?: string; fontWeightDefault?: number },
  colorScheme: 'light' | 'dark' = 'light',
): Record<string, string> {
  const base = colorScheme === 'dark' ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME

  // Start with complete default theme — every semantic CSS var is defined
  const style: Record<string, string> = {}
  for (const [cssVar, hsl] of Object.entries(base)) {
    style[`--${cssVar}`] = hsl
    style[`--color-${cssVar}`] = `hsl(${hsl})`
  }

  // Default radius
  style['--radius'] = '0.5rem'

  // Overlay project tokens (if any)
  if (tokens) {
    const overrides = getScopedTokenStyle(tokens)
    Object.assign(style, overrides)
  }

  // Force color-scheme for browser-level isolation
  style['colorScheme'] = colorScheme

  return style
}

/**
 * Remove any inline design-token CSS custom properties from document.documentElement.
 * Call this on mount in embedded contexts (e.g. Studio) to clean up stale
 * vars that a previous session's applyTokensToCSSVars() may have left behind.
 */
export function clearRootTokens(): void {
  if (typeof document === "undefined") return
  const root = document.documentElement
  for (const cssVar of Object.values(COLOR_KEY_TO_CSS)) {
    root.style.removeProperty(`--${cssVar}`)
  }
  root.style.removeProperty("--radius")
  root.style.removeProperty("--cogo-font-family")
  root.style.removeProperty("font-family")
  root.style.removeProperty("font-weight")
}

/**
 * Apply design tokens to CSS custom properties on a target element.
 * Defaults to document.documentElement for standalone apps (cogo-starter).
 * In embedded contexts (Studio), prefer getScopedTokenStyle() with a
 * wrapper div to avoid polluting the host's theme.
 */
export function applyTokensToCSSVars(tokens: DesignTokens, target?: HTMLElement): void {
  if (typeof document === "undefined") return
  const root = target ?? document.documentElement

  // Apply color tokens as HSL channels
  if (!tokens.colors) return
  for (const [key, cssVar] of Object.entries(COLOR_KEY_TO_CSS)) {
    const raw = resolveColorValue(tokens.colors, key, cssVar)
    if (!raw) continue
    const hsl = isHslChannels(raw) ? raw.trim() : hexToHslChannels(raw)
    if (hsl) {
      root.style.setProperty(`--${cssVar}`, hsl)
    }
  }

  // Apply border radius
  if (tokens.radius) {
    if (typeof tokens.radius === 'string') {
      root.style.setProperty("--radius", tokens.radius)
    } else {
      const radiusLg = tokens.radius.lg ?? tokens.radius.md
      if (radiusLg) root.style.setProperty("--radius", radiusLg)
    }
  }

  // Apply font-family
  if (tokens.fontFamily) {
    root.style.setProperty("--cogo-font-family", tokens.fontFamily)
    root.style.fontFamily = `${tokens.fontFamily}, sans-serif`
  }

  // Apply font-weight default
  if (tokens.fontWeightDefault) {
    root.style.fontWeight = String(tokens.fontWeightDefault)
  }
}
